<script lang="ts">
import { SelectRoot, SelectTrigger, SelectValue, SelectContent, SelectItem, SelectItemText, SelectViewport } from 'reka-ui';

export default {
  name: 'UniversalSearch',
  components: {
    SelectRoot,
    SelectTrigger,
    SelectValue,
    SelectContent,
    SelectItem,
  },
};
</script>

<script setup lang="ts">
import { ref, computed, watch, inject, nextTick, onMounted, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import { Store } from 'vuex';
import { universalSearchService } from '@/services/api';
import type { UniversalSearchResult, EntityType, EntityTypeOption } from '@/types/UniversalSearch';
import type { TabConfig } from '@/types/EntityTabs';
import { useEntityTabs } from '@/composables/useEntityTabs';
import type { AppState } from '@/store';
import { Building2Icon, BarChart3Icon, StoreIcon, SearchIcon, ChevronDown, X } from '@lucide/vue';
import { filters } from '@/utilities/Filters';

// -----
// data
// -----

const props = withDefaults(defineProps<{ autofocus?: boolean; modal?: boolean }>(), {
  autofocus: false,
  modal: false,
});

const emit = defineEmits<{ navigate: []; dismiss: [] }>();

const RESULT_LIMIT = 100;
const router = useRouter();
const store = inject<Store<AppState>>('store');
const { getTabs, loading: tabsLoading } = useEntityTabs();

// Reactive state
const searchQuery = ref('');
const selectedEntityType = ref<EntityType | 'all' | undefined>();
const results = ref<UniversalSearchResult[]>([]);
const activeIndex = ref(-1);
const inTabsPane = ref(false);
const activeTabIndex = ref(-1);
const loading = ref(false);
const hasSearched = ref(false);
const entityTabs = ref<TabConfig[]>([]);
const searchResultsRef = ref<HTMLElement>();
const resultsListRef = ref<HTMLElement>();
const tabsListRef = ref<HTMLElement>();
const searchInputRef = ref<HTMLInputElement>();

// Non-reactive state
let searchTimeout: NodeJS.Timeout;
let entityTabsTimeout: NodeJS.Timeout;
// Tabs for an entity never change within a session, so a row revisited by arrowing back up
// repaints from here instead of refetching.
const entityTabsCache = new Map<string, TabConfig[]>();

const ENTITY_TABS_DEBOUNCE_MS = 200;

// Constants
const entityTypeOptions: EntityTypeOption[] = [
  { value: 'account', label: 'Clients', icon: Building2Icon },
  { value: 'carrier', label: 'Carriers', icon: BarChart3Icon },
  { value: 'wholesaler', label: 'Wholesalers', icon: StoreIcon },
];

const ICONS_BY_TYPE = {
  account: Building2Icon,
  carrier: BarChart3Icon,
  wholesaler: StoreIcon,
};

const CHIP_CLASSES_BY_TYPE = {
  account: 'bg-blue-100 text-blue-800',
  carrier: 'bg-green-100 text-green-800',
  wholesaler: 'bg-orange-100 text-orange-800',
};

const ICON_COLORS_BY_TYPE = {
  account: '#1e40af', // blue-800
  carrier: '#166534', // green-800
  wholesaler: '#9a3412', // orange-800
};

const LABELS_BY_TYPE = {
  account: 'Client',
  carrier: 'Carrier',
  wholesaler: 'Wholesaler',
};

// -----
// computed
// -----

const loadingTabs = computed(() => tabsLoading.value);

const activeResult = computed<UniversalSearchResult | null>(() => results.value[activeIndex.value] ?? null);

// Sanitising and marking up each name is the expensive part of a row, and it depends only on
// the name and the query — deriving it once keeps arrow keys off that work entirely.
const highlightedNames = computed(() => results.value.map((result) => filters.highlight(result.name, searchQuery.value)));

const activeDescendantId = computed(() => {
  if (inTabsPane.value && activeTabIndex.value >= 0) {
    return `universal-search-tab-${activeTabIndex.value}`;
  }
  return activeIndex.value >= 0 ? `universal-search-result-${activeIndex.value}` : undefined;
});

const shouldShowResults = computed(() => {
  return searchQuery.value.trim().length > 0 && (loading.value || results.value.length > 0 || hasSearched.value);
});

const searchPlaceholder = computed(() => {
  if (!selectedEntityType.value || selectedEntityType.value === 'all') {
    return 'Search clients, carriers, and wholesalers';
  }

  const typeOption = entityTypeOptions.find((option) => option.value === selectedEntityType.value);
  return typeOption ? `Search ${typeOption.label.toLowerCase()}` : 'Search...';
});

// -----
// methods
// -----

const getIconForType = (type: EntityType) => ICONS_BY_TYPE[type];

const getChipClassForType = (result: UniversalSearchResult): string =>
  result.isProspect ? 'bg-blue-50 text-blue-800' : CHIP_CLASSES_BY_TYPE[result.resultType];

const getIconColorForType = (type: EntityType): string => ICON_COLORS_BY_TYPE[type];

const getTypeLabel = (result: UniversalSearchResult): string =>
  result.isProspect ? 'Prospect' : LABELS_BY_TYPE[result.resultType];

const sortResults = (results: UniversalSearchResult[], searchQuery: string): UniversalSearchResult[] => {
  if (!searchQuery || !searchQuery.trim()) {
    return results.sort((a, b) => a.name.localeCompare(b.name));
  }

  const query = searchQuery.toLowerCase().trim();

  return results.sort((a, b) => {
    const aName = a.name.toLowerCase();
    const bName = b.name.toLowerCase();

    const aStartsWithQuery = aName.startsWith(query);
    const bStartsWithQuery = bName.startsWith(query);

    const aContainsQuery = aName.includes(query);
    const bContainsQuery = bName.includes(query);

    // First priority: items that start with the search query
    if (aStartsWithQuery && !bStartsWithQuery) return -1;
    if (!aStartsWithQuery && bStartsWithQuery) return 1;

    // If both start with query or neither starts with query, check if they contain query
    if (aStartsWithQuery === bStartsWithQuery) {
      // Second priority: items that contain the search query
      if (aContainsQuery && !bContainsQuery) return -1;
      if (!aContainsQuery && bContainsQuery) return 1;

      // If both have same match priority, sort alphabetically
      return aName.localeCompare(bName);
    }

    return 0;
  });
};

const navigateToTab = (tab: TabConfig) => {
  router.push(tab.route);
  emit('navigate');
};

const performSearch = async () => {
  activeIndex.value = -1;

  if (!searchQuery.value.trim()) {
    results.value = [];
    hasSearched.value = false;
    return;
  }

  loading.value = true;
  hasSearched.value = true;

  try {
    const searchResults = await universalSearchService.search({
      search: searchQuery.value,
      entityTypes: selectedEntityType.value && selectedEntityType.value !== 'all' ? [selectedEntityType.value] : undefined,
    });
    results.value = sortResults(searchResults, searchQuery.value).slice(0, RESULT_LIMIT);
  } catch (error) {
    console.error('Search error:', error);
    results.value = [];
  } finally {
    activeIndex.value = -1;
    loading.value = false;
  }
};

const onSearchInput = () => {
  leaveTabsPane();

  // Debounce search
  clearTimeout(searchTimeout);
  searchTimeout = setTimeout(performSearch, 300);
};

const entityTabsKey = (result: UniversalSearchResult) => `${result.resultType}:${result.id}`;

const loadEntityTabs = async (result: UniversalSearchResult) => {
  if (!store?.state.user) {
    return;
  }

  try {
    const tabs = await getTabs(result.resultType, result.id.toString());
    entityTabsCache.set(entityTabsKey(result), tabs.value);
    // Tabs for an account cost two extra requests, so a slow response for a row the highlight
    // has already left must not overwrite the row the user is on now.
    if (activeResult.value === result) {
      entityTabs.value = tabs.value;
    }
  } catch (error) {
    console.error('Error loading entity tabs:', error);
    if (activeResult.value === result) {
      entityTabs.value = [];
    }
  }
};

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

const moveActiveIndex = (offset: number) => {
  if (inTabsPane.value) {
    activeTabIndex.value = clamp(activeTabIndex.value + offset, 0, entityTabs.value.length - 1);
    return;
  }

  if (!results.value.length) {
    return;
  }
  // Floors at -1, not 0: arrowing up off the top clears the selection rather than sticking.
  activeIndex.value = clamp(activeIndex.value + offset, -1, results.value.length - 1);
};

const leaveTabsPane = () => {
  inTabsPane.value = false;
  activeTabIndex.value = -1;
};

// Right doubles as caret movement, so it only crosses into the panel from the end of the query.
const onArrowRight = (event: KeyboardEvent) => {
  const input = event.target as HTMLInputElement;
  const caretAtEnd = input.selectionStart === searchQuery.value.length && input.selectionEnd === input.selectionStart;

  if (inTabsPane.value || !activeResult.value || !entityTabs.value.length || !caretAtEnd) {
    return;
  }

  event.preventDefault();
  inTabsPane.value = true;
  activeTabIndex.value = 0;
};

const onArrowLeft = (event: KeyboardEvent) => {
  if (!inTabsPane.value) {
    return;
  }
  event.preventDefault();
  leaveTabsPane();
};

const onEnter = () => {
  if (inTabsPane.value) {
    const tab = entityTabs.value[activeTabIndex.value];
    if (tab) {
      navigateToTab(tab);
    }
    return;
  }

  if (activeResult.value) {
    navigateToResult(activeResult.value);
    return;
  }
  performSearch();
};

const navigateToResult = (result: UniversalSearchResult) => {
  const routeMap = {
    account: `/account/${result.id}/overview`,
    carrier: `/carrier/${result.id}/overview`,
    wholesaler: `/wholesaler-group/${result.id}/overview`,
  };

  const route = routeMap[result.resultType];
  if (route) {
    router.push(route);
    emit('navigate');
  }
};

// -----
// watchers
// -----

watch(selectedEntityType, () => {
  if (searchQuery.value) {
    performSearch();
  }
});

watch(activeResult, (result) => {
  leaveTabsPane();
  clearTimeout(entityTabsTimeout);
  // Clear straight away so the previous result's tabs never show under a new result's name.
  entityTabs.value = [];

  if (!result) {
    return;
  }

  const cached = entityTabsCache.get(entityTabsKey(result));
  if (cached) {
    entityTabs.value = cached;
    return;
  }

  entityTabsTimeout = setTimeout(() => loadEntityTabs(result), ENTITY_TABS_DEBOUNCE_MS);
});

const scrollActiveIntoView = async (list: HTMLElement | undefined, selector: string) => {
  await nextTick();
  list?.querySelector(selector)?.scrollIntoView({ block: 'nearest' });
};

watch(activeIndex, (index) => {
  if (index >= 0) {
    scrollActiveIntoView(resultsListRef.value, `[data-result-index="${index}"]`);
  }
});

watch(activeTabIndex, (index) => {
  if (index >= 0) {
    scrollActiveIntoView(tabsListRef.value, `[data-tab-index="${index}"]`);
  }
});

// -----
// click outside handling
// -----

const handleClickOutside = (event: MouseEvent) => {
  const target = event.target as HTMLElement;
  const searchContainer = searchResultsRef.value?.closest('.universal-search');

  // Don't close if clicking within the search container
  if (searchContainer && searchContainer.contains(target)) {
    return;
  }

  // Close search results
  searchQuery.value = '';
  results.value = [];
  hasSearched.value = false;
  activeIndex.value = -1;
};

// -----
// lifecycle
// -----

onMounted(() => {
  // Inside the modal the only "outside" is the backdrop, and the host remounts this component
  // on every open — resetting there would just wipe the query on a stray backdrop click.
  if (!props.modal) {
    document.addEventListener('click', handleClickOutside);
  }

  if (props.autofocus) {
    searchInputRef.value?.focus();
  }
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
  // The modal unmounts this on every close, so an armed timer would otherwise fire a search or
  // a tab fetch for a panel that no longer exists.
  clearTimeout(searchTimeout);
  clearTimeout(entityTabsTimeout);
});
</script>

<template>
  <div
    class="universal-search mx-auto max-w-3xl shadow-md"
    :class="shouldShowResults ? 'rounded-t-lg' : 'rounded-lg'">
    <!-- Search Bar -->
    <div
      class="search-container flex items-center gap-3 p-2"
      :class="shouldShowResults ? 'relative z-[60] rounded-t-lg shadow-sm' : 'rounded-lg'">
      <!-- Type Filter Dropdown -->
      <div class="relative shrink-0">
        <SelectRoot v-model="selectedEntityType">
          <SelectTrigger
            as-child
            class="inline-flex h-[35px] min-w-[120px] items-center justify-between gap-[5px] rounded-lg border bg-white px-[15px] text-xs leading-none text-gray-700 shadow-xs outline-none hover:bg-gray-50 focus:shadow-[0_0_0_2px] focus:shadow-blue-500 data-[state=open]:bg-gray-50">
            <button
              type="button"
              class="flex w-full items-center justify-between">
              <SelectValue placeholder="All Types" />
              <ChevronDown class="h-3 w-3 text-gray-400" />
            </button>
          </SelectTrigger>

          <SelectContent
            class="z-100 min-w-[160px] rounded-lg border bg-white shadow-lg"
            position="popper"
            :side-offset="5">
            <SelectViewport class="p-[5px]">
              <SelectItem
                value="all"
                class="relative flex h-[25px] cursor-pointer items-center rounded-[3px] pr-[35px] pl-[25px] text-xs leading-none text-gray-700 select-none hover:bg-blue-100 hover:outline-none data-[highlighted]:bg-blue-100 data-[highlighted]:outline-none">
                <SelectItemText>All Types</SelectItemText>
              </SelectItem>
              <SelectItem
                v-for="option in entityTypeOptions"
                :key="option.value"
                :value="option.value"
                class="relative flex h-[25px] cursor-pointer items-center gap-2 rounded-[3px] pr-[35px] pl-[25px] text-xs leading-none text-gray-700 select-none hover:bg-blue-100 hover:outline-none data-[highlighted]:bg-blue-100 data-[highlighted]:outline-none">
                <component
                  :is="getIconForType(option.value)"
                  class="h-3 w-3" />
                <SelectItemText>{{ option.label }}</SelectItemText>
              </SelectItem>
            </SelectViewport>
          </SelectContent>
        </SelectRoot>
      </div>

      <!-- Search Input -->
      <div class="relative flex-1">
        <div class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
          <SearchIcon class="h-4 w-4 text-gray-400" />
        </div>
        <input
          ref="searchInputRef"
          v-model="searchQuery"
          type="text"
          :placeholder="searchPlaceholder"
          class="block w-full rounded-md border border-gray-300 py-2 pl-10 text-sm placeholder-gray-400 focus:border-transparent focus:ring-2 focus:ring-blue-500 focus:outline-none"
          :class="modal ? 'pr-11' : 'pr-3'"
          role="combobox"
          aria-controls="universal-search-results"
          :aria-expanded="shouldShowResults"
          :aria-activedescendant="activeDescendantId"
          @keydown.down.prevent="moveActiveIndex(1)"
          @keydown.up.prevent="moveActiveIndex(-1)"
          @keydown.right="onArrowRight"
          @keydown.left="onArrowLeft"
          @keydown.enter="onEnter"
          @input="onSearchInput" />
        <button
          v-if="modal"
          type="button"
          aria-label="Close search"
          data-testid="universal-search-close"
          class="absolute inset-y-0 right-2 my-auto flex h-7 w-7 cursor-pointer items-center justify-center rounded-full border border-gray-300 bg-white text-gray-500 hover:border-gray-400 hover:bg-gray-50 hover:text-gray-700"
          @click="emit('dismiss')">
          <X class="h-4 w-4" />
        </button>
      </div>
    </div>

    <!-- Search Results -->
    <div
      ref="searchResultsRef"
      class="search-results absolute top-full -right-px -left-px z-50 flex h-[555px] rounded-b-lg border border-t-0 bg-white shadow-lg"
      v-if="shouldShowResults">
      <!-- Results List -->
      <div
        ref="resultsListRef"
        class="results-list w-1/2 overflow-y-auto border-r">
        <div
          v-if="loading"
          class="p-4 text-center">
          <div class="inline-block h-6 w-6 animate-spin rounded-full border-b-2 border-blue-500"></div>
          <p class="mt-2 text-sm text-gray-600">Searching...</p>
        </div>

        <div
          v-else-if="results.length === 0 && hasSearched"
          class="p-4 text-center text-gray-500">
          <p class="text-sm">No results found for "{{ searchQuery }}"</p>
        </div>

        <div
          v-else
          id="universal-search-results"
          role="listbox">
          <div
            v-for="(result, index) in results"
            :id="`universal-search-result-${index}`"
            :key="`${result.resultType}-${result.id}`"
            :data-result-index="index"
            role="option"
            :aria-selected="index === activeIndex"
            class="result-item flex cursor-pointer items-center gap-3 border-b p-3"
            :class="{ 'bg-gray-200': index === activeIndex }"
            @click="navigateToResult(result)"
            @mouseenter="activeIndex = index">
            <!-- Type Icon -->
            <component
              :is="getIconForType(result.resultType)"
              class="h-4 w-4"
              :color="getIconColorForType(result.resultType)" />

            <!-- Content -->
            <div class="min-w-0 flex-1">
              <div
                class="truncate text-sm font-medium"
                v-html="highlightedNames[index]"></div>
              <div
                v-if="result.subtitle"
                class="truncate text-xs text-gray-600">
                {{ result.subtitle }}
              </div>
              <div class="mt-1 flex items-center gap-2">
                <span :class="`inline-flex items-center rounded-full px-3 py-1 text-xs font-medium ${getChipClassForType(result)}`">
                  {{ getTypeLabel(result) }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Context Panel -->
      <div
        class="context-panel flex w-1/2 flex-col bg-gray-50 p-3"
        data-testid="universal-search-context">
        <div
          v-if="activeResult"
          class="flex min-h-0 flex-1 flex-col">
          <div class="mb-3 flex w-full items-center gap-2">
            <component
              :is="getIconForType(activeResult.resultType)"
              class="h-4 w-4"
              :color="getIconColorForType(activeResult.resultType)" />
            <div>
              <h3 class="text-sm font-medium">{{ activeResult.name }}</h3>
              <p class="text-xs text-gray-600">{{ getTypeLabel(activeResult) }}</p>
            </div>
            <span
              v-if="activeResult.additionalInfo"
              class="mr-0 ml-auto self-start text-xs text-gray-500"
              >{{ activeResult.additionalInfo }}</span
            >
          </div>

          <!-- Tabs Navigation -->
          <div
            class="flex min-h-0 flex-1 flex-col rounded border bg-white p-2 px-2 transition-colors"
            :class="{ 'border-blue-400 ring-1 ring-blue-400': inTabsPane }">
            <h4 class="mb-2 shrink-0 text-xs font-medium text-gray-700">Quick Navigation</h4>
            <div
              ref="tabsListRef"
              role="listbox"
              class="min-h-0 flex-1 space-y-1 overflow-y-auto">
              <template v-if="!loadingTabs">
                <button
                  v-for="(tab, tabIndex) in entityTabs"
                  :id="`universal-search-tab-${tabIndex}`"
                  :key="tab.key"
                  v-show="tab.visible"
                  :data-tab-index="tabIndex"
                  role="option"
                  :aria-selected="inTabsPane && tabIndex === activeTabIndex"
                  class="flex w-full cursor-pointer items-center gap-2 rounded p-2 text-left text-xs focus:outline-none"
                  :class="{ 'bg-gray-200': inTabsPane && tabIndex === activeTabIndex, 'hover:bg-gray-200': !inTabsPane }"
                  @click="navigateToTab(tab)">
                  <component
                    :is="tab.icon"
                    v-if="tab.icon"
                    class="h-3 w-3 text-gray-500" />
                  <span class="text-gray-700">{{ tab.label }}</span>
                </button>
                <div
                  v-if="entityTabs.length === 0"
                  class="flex items-center justify-center py-8">
                  <p class="text-xs text-gray-500">No navigation items available</p>
                </div>
              </template>
            </div>
          </div>
        </div>

        <div
          v-else
          class="flex h-full items-center justify-center text-center">
          <div class="text-gray-500">
            <SearchIcon class="mx-auto mb-2 h-8 w-8 text-gray-400" />
            <p class="text-sm">Use the arrow keys or hover over a result to see more details</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
@reference "@/assets/styles/global.css";

.universal-search {
  @apply relative border bg-white shadow-md;
}

.search-container {
  @apply bg-gray-50;
}

.result-item:last-child {
  @apply border-b-0;
}

.type-filter :deep(.v-field) {
  @apply min-h-0;
}
</style>
